import React, { useState, useCallback } from 'react';
import { useInput } from 'react-admin';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';

function objectToRows(obj) {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj).map(([key, value]) => ({ key, value }));
}

function rowsToObject(rows) {
  const obj = {};
  for (const row of rows) {
    const key = (row.key || '').trim();
    if (key) {
      obj[key] = row.value || '';
    }
  }
  return obj;
}

export function KeyValueInput({ source, label, keyLabel = 'Key', valueLabel = 'Value', addActionLabel = 'Add Key-Value', helperText, ...rest }) {
  const {
    input: { value, onChange },
  } = useInput({ source, ...rest });

  const [rows, setRows] = useState(() => {
    const initial = objectToRows(value);
    return initial.length > 0 ? initial : [];
  });

  const updateRows = useCallback((newRows) => {
    setRows(newRows);
    onChange(rowsToObject(newRows));
  }, [onChange]);

  const handleKeyChange = useCallback((index, newKey) => {
    const newRows = rows.map((row, i) => i === index ? { ...row, key: newKey } : row);
    updateRows(newRows);
  }, [rows, updateRows]);

  const handleValueChange = useCallback((index, newValue) => {
    const newRows = rows.map((row, i) => i === index ? { ...row, value: newValue } : row);
    updateRows(newRows);
  }, [rows, updateRows]);

  const handleAdd = useCallback(() => {
    updateRows([...rows, { key: '', value: '' }]);
  }, [rows, updateRows]);

  const handleRemove = useCallback((index) => {
    updateRows(rows.filter((_, i) => i !== index));
  }, [rows, updateRows]);

  return (
    <Box style={{ gridColumn: '1 / -1', marginBottom: 16 }}>
      {label && <Typography variant="subtitle2" style={{ marginBottom: 8 }}>{label}</Typography>}
      {helperText && <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: 'block' }}>{helperText}</Typography>}
      {rows.map((row, index) => (
        <Box key={index} display="flex" alignItems="center" style={{ marginBottom: 8, gap: 8 }}>
          <TextField
            value={row.key}
            onChange={(e) => handleKeyChange(index, e.target.value)}
            label={keyLabel}
            variant="filled"
            size="small"
            style={{ flex: 1 }}
          />
          <TextField
            value={row.value}
            onChange={(e) => handleValueChange(index, e.target.value)}
            label={valueLabel}
            variant="filled"
            size="small"
            style={{ flex: 1 }}
          />
          <IconButton size="small" onClick={() => handleRemove(index)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button size="small" color="primary" startIcon={<AddIcon />} onClick={handleAdd}>
        {addActionLabel}
      </Button>
    </Box>
  );
}
